/**
 * @file 広い画面かを返す (900px 以上を広いとする)。リーダーと全画面ビューワが同じ境目を使う。
 * @failure: jsdom 等 matchMedia が無い環境は狭いとみなす。
 */

import { useSyncExternalStore } from "react";

const QUERY = "(min-width: 900px)";

/**
 * 幅が変わるたびに出し直す形で広い画面かを返す。
 *
 * wideScreen() は最初の1回だけを見るので、初期値を決めるのに使う。こちらはヘッダーの畳み方の
 * ように、画面を回したりブラウザの幅を変えたりしたその場で切り替わってほしいところで使う。
 */
export function useWideScreen(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== "function") return () => {};
      const mq = window.matchMedia(QUERY);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    wideScreen,
    () => false,
  );
}

export function wideScreen(): boolean {
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(min-width: 900px)").matches
    : false;
}

const SPREAD_QUERY = "(orientation: landscape) and (min-width: 900px) and (min-height: 600px)";

/**
 * 見開きで並べられる画面かを返す (横長で、広さが足りる画面)。画面を回したらその場で切り替わる。
 *
 * @why: スマホや iPad の縦向きで2ページ並べると、文字が読めない大きさまで縮む (代表の判断、2026-10-13)。
 *   横向きのスマホは縦の高さが足りないので、高さも見る。
 */
export function useSpreadScreen(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window.matchMedia !== "function") return () => {};
      const mq = window.matchMedia(SPREAD_QUERY);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    },
    () => (typeof window.matchMedia === "function" ? window.matchMedia(SPREAD_QUERY).matches : false),
    () => false,
  );
}

/**
 * 開いているパネルが「読む画面から離れた」に当たるかを返す。
 *
 * 狭い画面ではパネルが紙面を置き換えるので離れたことになる。広い画面では紙面の隣に並ぶだけ
 * なので、開いていてもふつうに読んでいる扱いにする。※なぜなら広い画面はメモを開いた状態で
 * 始まり、開いた直後から「読むだけにもどる」が出ると、何かがおかしいように見えるためである。
 */
export function panelIsOffPlain(openPanelId: string | null): boolean {
  return openPanelId !== null && !wideScreen();
}
